import * as React from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/** A one-shot confirmation. An uncertain provider result must never auto-retry. */
export function CreditKeyConfirmDialog({
  title,
  description,
  confirmLabel,
  failureMessage,
  destructive = false,
  requireReason = true,
  optionalReason = false,
  acknowledgement,
  onConfirm,
  onClose,
}: {
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  failureMessage: string;
  destructive?: boolean;
  requireReason?: boolean;
  optionalReason?: boolean;
  acknowledgement?: string;
  onConfirm: (reason: string) => Promise<void>;
  onClose: () => void;
}) {
  const [reason, setReason] = React.useState("");
  const [reasonError, setReasonError] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const [acknowledged, setAcknowledged] = React.useState(false);
  const inFlight = React.useRef(false);
  const reasonRef = React.useRef<HTMLTextAreaElement>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const opener = React.useRef(typeof document === "undefined" ? null : document.activeElement);
  const id = React.useId();

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (inFlight.current || failed || (acknowledgement && !acknowledged)) return;
    const trimmedReason = reason.trim();
    if ((requireReason && trimmedReason.length < 8) || ((requireReason || optionalReason) && trimmedReason.length > 500)) {
      setReasonError(true);
      reasonRef.current?.focus();
      return;
    }
    inFlight.current = true;
    setPending(true);
    try {
      await onConfirm(trimmedReason);
      onClose();
    } catch {
      // Raw provider/auth errors can contain identifiers. Keep the explanation
      // local, and require a fresh status check before another payment request.
      setFailed(true);
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !inFlight.current) onClose(); }}>
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain"
        showCloseButton={!pending}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          cancelRef.current?.focus();
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (opener.current instanceof HTMLElement && opener.current.isConnected) opener.current.focus();
        }}
        onEscapeKeyDown={(event) => { if (inFlight.current) event.preventDefault(); }}
        onPointerDownOutside={(event) => { if (inFlight.current) event.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form className="space-y-5" onSubmit={submit} noValidate>
          {acknowledgement ? <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" className="mt-1" checked={acknowledged} disabled={pending || failed} onChange={(event) => setAcknowledged(event.target.checked)} />
            <span>{acknowledgement}</span>
          </label> : null}
          {requireReason || optionalReason ? (
            <div className="space-y-2">
              <Label htmlFor={`${id}-reason`}>Reason ({requireReason ? "required" : "optional"})</Label>
              <Textarea
                ref={reasonRef}
                id={`${id}-reason`}
                name="creditKeyReason"
                value={reason}
                maxLength={500}
                required={requireReason}
                disabled={pending || failed}
                aria-invalid={reasonError || undefined}
                aria-describedby={`${id}-reason-help`}
                onChange={(event) => { setReason(event.target.value); setReasonError(false); }}
              />
              <p id={`${id}-reason-help`} className={reasonError ? "text-sm text-destructive" : "text-xs text-muted-foreground"}>
                {requireReason
                  ? reasonError ? "Enter a reason between 8 and 500 characters." : "Use 8–500 characters. This is saved in the staff audit trail; do not include bank details or credentials."
                  : "Add a note if helpful (up to 500 characters)."}
              </p>
            </div>
          ) : null}
          {failed ? <p role="alert" className="text-sm text-destructive">{failureMessage}</p> : null}
          <DialogFooter>
            <Button ref={cancelRef} type="button" variant="outline" disabled={pending} onClick={onClose}>
              {failed ? "Close" : "Keep unchanged"}
            </Button>
            <Button type="submit" variant={destructive ? "destructive" : "default"} disabled={pending || failed || (!!acknowledgement && !acknowledged) || (requireReason && reason.trim().length < 8) || ((requireReason || optionalReason) && reason.trim().length > 500)} aria-busy={pending}>
              {pending ? <Loader2 aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" /> : null}
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
